import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * SplitPanelLayout — two-pane view with a draggable divider.
 *
 * Left pane = conversation (FIXED, always visible — primary interaction).
 * Right pane = document editor (COLLAPSIBLE + MAXIMIZABLE — secondary,
 *              expands when a draft is generated).
 *
 * Props
 *   leftContent          — ReactNode
 *   rightContent         — ReactNode
 *   initialSplit         — number 10..90 (default 50)
 *   onSplitChange        — (percent: number) => void
 *   minLeft              — number (default 280 px)
 *   minRight             — number (default 320 px)
 *   className            — string
 *   harveyTheme          — boolean (default false) → adds data-theme="harvey"
 *   rightCollapsed       — controlled boolean (optional). When provided,
 *                          the parent owns collapse state.
 *   onRightCollapsedChange — (next: boolean) => void
 *   rightCollapsedLabel  — { ar, en } shown vertically on collapsed strip
 */
export const SplitPanelLayout = ({
  leftContent,
  rightContent,
  initialSplit = 50,
  onSplitChange,
  minLeft = 280,
  minRight = 320,
  className,
  harveyTheme = false,
  rightCollapsed: rightCollapsedProp,
  onRightCollapsedChange,
  rightCollapsedLabel = { ar: 'المستند', en: 'Document' },
  /**
   * `hideRightOnMobile` — when true AND the viewport is ≤768px, the
   * right pane is omitted entirely so the left (chat) gets full height.
   * Used by AgentPreviewPage when there is no draft / review / loading
   * state worth showing — otherwise the "Generated drafts will appear
   * here…" placeholder would steal 55vh from the chat on every empty
   * conversation (May-24 mobile audit, item #5).
   */
  hideRightOnMobile = false,
}) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const [split, setSplit] = useState(clamp(initialSplit, 10, 90));
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isControlled = typeof rightCollapsedProp === 'boolean';
  const rightCollapsed = isControlled ? rightCollapsedProp : internalCollapsed;
  const setRightCollapsed = useCallback((next) => {
    const v = typeof next === 'function' ? next(rightCollapsed) : next;
    if (!isControlled) setInternalCollapsed(v);
    onRightCollapsedChange?.(v);
  }, [isControlled, rightCollapsed, onRightCollapsedChange]);

  const [rightMaximized, setRightMaximized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // I.F (Cluster I, Feb 2026): mobile tab strip state. Defaults to
  // "left" (chat) — the empty-state. We auto-flip to "right" the
  // first time the host gives us non-empty rightContent so the user
  // is taken to the freshly produced draft without an extra tap.
  const [mobileActivePane, setMobileActivePane] = useState('left');
  const prevHadRightRef = useRef(false);
  useEffect(() => {
    const hasRight = Boolean(rightContent) && !hideRightOnMobile;
    if (isMobile && hasRight && !prevHadRightRef.current) {
      setMobileActivePane('right');
    }
    prevHadRightRef.current = hasRight;
  }, [isMobile, rightContent, hideRightOnMobile]);

  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onSplit = useCallback((next) => {
    const c = clamp(next, 10, 90);
    setSplit(c);
    onSplitChange?.(c);
  }, [onSplitChange]);

  // ── Drag handling ────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let pct = ((clientX - rect.left) / rect.width) * 100;
      if (isArabic) pct = 100 - pct;
      const totalPx = rect.width;
      const minLeftPct = (minLeft / totalPx) * 100;
      const minRightPct = (minRight / totalPx) * 100;
      pct = clamp(pct, minLeftPct, 100 - minRightPct);
      onSplit(pct);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isArabic, minLeft, minRight, onSplit]);

  // ── Mobile: tab-strip layout (Cluster I.F, Feb 2026) ────────────
  if (isMobile) {
    // I.F decision: at < 768 px the side-by-side split + stacked
    // fallback both leave the user with two cramped panes. Replace
    // with a top tab strip `[ Chat | Draft ]` and full-viewport
    // panes — single thing on screen at a time, both panes get full
    // height. When the right pane has nothing real to show, drop
    // the Draft tab and render Chat full-screen.
    if (hideRightOnMobile) {
      return (
        <div
          ref={containerRef}
          className={cn('flex flex-col w-full h-full', className)}
          data-theme={harveyTheme ? 'harvey' : undefined}
          data-testid="split-panel-layout"
          data-mobile-right-hidden="true"
        >
          <div className="flex-1 min-h-0 overflow-auto" data-testid="split-panel-left">{leftContent}</div>
        </div>
      );
    }
    return (
      <div
        ref={containerRef}
        className={cn('flex flex-col w-full h-full', className)}
        data-theme={harveyTheme ? 'harvey' : undefined}
        data-testid="split-panel-layout"
        data-mobile-tabs="true"
      >
        {/* I.F tab strip — only mounts on mobile. Sticky at top so
            it survives long-scroll chats / drafts. */}
        <div
          className="flex border-b sticky top-0 bg-white"
          style={{
            borderColor: 'rgba(0,0,0,0.08)',
            flexDirection: isArabic ? 'row-reverse' : 'row',
            zIndex: 'var(--z-sticky-header, 20)',
          }}
          data-testid="mobile-pane-tabs"
        >
          {[
            { key: 'left',  label: isArabic ? 'المحادثة' : 'Chat' },
            { key: 'right', label: isArabic ? 'المستند' : 'Draft' },
          ].map((t) => {
            const active = mobileActivePane === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setMobileActivePane(t.key)}
                data-testid={`mobile-pane-tab-${t.key}`}
                data-active={active}
                className="flex-1 px-4 py-3 text-sm font-medium transition-colors"
                style={{
                  color: active ? '#111' : '#666',
                  borderBottom: active ? '2px solid #111' : '2px solid transparent',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div
          className="flex-1 min-h-0 overflow-auto"
          data-testid={`split-panel-${mobileActivePane}`}
        >
          {mobileActivePane === 'left' ? leftContent : rightContent}
        </div>
      </div>
    );
  }

  // ── Desktop ─────────────────────────────────────────────────────
  // Inverted: left pane is FIXED (no collapse). Right pane is collapsible
  // (becomes a thin vertical-label strip) and maximizable (covers the full
  // width). When right is maximized, left dims out.
  const leftWidthCss = rightMaximized
    ? 0
    : (rightCollapsed ? `calc(100% - 48px)` : `${split}%`);
  const rightWidthCss = rightMaximized
    ? '100%'
    : (rightCollapsed ? 48 : `${100 - split}%`);

  // The expand chevron points "into the document" — i.e. away from the chat.
  // In RTL the layout is mirrored, so flip the icon accordingly.
  const expandIcon = isArabic ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />;
  const collapseIcon = isArabic ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex w-full h-full overflow-hidden',
        isArabic && 'flex-row-reverse',
        className,
      )}
      data-theme={harveyTheme ? 'harvey' : undefined}
      data-testid="split-panel-layout"
    >
      {/* Left pane — FIXED. No collapse button. */}
      <aside
        className={cn(
          'flex flex-col h-full overflow-hidden hv-border-r transition-[width] duration-200',
          rightMaximized && 'opacity-0 pointer-events-none',
        )}
        style={{ width: leftWidthCss, minWidth: minLeft }}
        data-testid="split-panel-left"
      >
        <div className="flex-1 overflow-auto">{leftContent}</div>
      </aside>

      {/* Divider — only shown when right pane is in normal split mode */}
      {!rightCollapsed && !rightMaximized && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={isArabic ? 'تغيير حجم الألواح' : 'Resize panels'}
          tabIndex={0}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') onSplit(split + (isArabic ? 2 : -2));
            if (e.key === 'ArrowRight') onSplit(split + (isArabic ? -2 : 2));
          }}
          className={cn(
            'w-[2px] cursor-col-resize bg-black/10 hover:bg-black/30 transition-colors shrink-0',
          )}
          data-testid="split-panel-divider"
        />
      )}

      {/* Right pane — COLLAPSIBLE + MAXIMIZABLE */}
      <main
        className="flex flex-col h-full overflow-hidden hv-border-l transition-[width] duration-200"
        style={{ width: rightWidthCss, minWidth: rightCollapsed ? 48 : minRight }}
        data-testid="split-panel-right"
      >
        {rightCollapsed ? (
          // ── Collapsed strip ──────────────────────────────────────
          <button
            type="button"
            onClick={() => setRightCollapsed(false)}
            className="flex-1 w-full flex flex-col items-center justify-start gap-3 py-4 hover:bg-black/5 transition-colors"
            aria-label={isArabic ? 'فتح لوحة المستند' : 'Expand document panel'}
            data-testid="split-panel-right-expand"
          >
            {expandIcon}
            <span
              className="hv-text-muted text-xs tracking-widest uppercase"
              style={{ writingMode: 'vertical-rl', letterSpacing: '0.2em' }}
            >
              {isArabic ? rightCollapsedLabel.ar : rightCollapsedLabel.en}
            </span>
          </button>
        ) : (
          <>
            <div className="flex items-center justify-end gap-1 px-2 py-2 hv-border-b shrink-0">
              <button
                type="button"
                onClick={() => setRightCollapsed(true)}
                className="p-1 rounded hover:bg-black/5"
                aria-label={isArabic ? 'طي لوحة المستند' : 'Collapse document panel'}
                data-testid="split-panel-right-collapse"
              >
                {collapseIcon}
              </button>
              <button
                type="button"
                onClick={() => setRightMaximized((v) => !v)}
                className="p-1 rounded hover:bg-black/5"
                aria-label={rightMaximized
                  ? (isArabic ? 'استعادة العرض المنقسم' : 'Restore split')
                  : (isArabic ? 'تكبير المستند' : 'Maximize editor')}
                data-testid="split-panel-maximize-toggle"
              >
                {rightMaximized
                  ? <Minimize2 className="w-4 h-4" />
                  : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex-1 overflow-auto">{rightContent}</div>
          </>
        )}
      </main>
    </div>
  );
};

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

export default SplitPanelLayout;
